/**
 * Optimization Service
 * SEO Manager — Optimization Section (Ecosystem Monitor & Monthly Insights)
 * Connects to /api/v1/optimization endpoints
 */

import { API_BASE_URL } from './api';

export interface DataSources {
  gsc: boolean;
  ga4: boolean;
  rank_tracking: boolean;
  serp: boolean;
}

export interface SectionStatus {
  declines?: string;
  competitors?: string;
  opportunities?: string;
  updates?: string;
  skip_reason?: string | null;
}

export interface SnapshotMeta {
  snapshot_id?: string | null;
  kind?: 'initial' | 'monthly' | string | null;
  period_month?: string | null;
  generated_at?: string | null;
  next_refresh_at?: string | null;
  data_sources?: DataSources;
  section_status?: Record<string, string>;
  empty_reason?: 'BASELINE_RECORDED' | 'NONE_DETECTED' | 'NO_SNAPSHOT' | string | null;
}

export interface StandardResponse<T> {
  status: string;
  message: string;
  data?: T;
  meta?: SnapshotMeta;
}

// ── 1. Status ─────────────────────────────────────────────────────────────────

export interface LatestSnapshotInfo {
  id: string;
  period_month: string;
  status: string;
}

export interface FeatureFlags {
  ecosystem_monitor: boolean;
  foresight_recommends: boolean;
}

export interface OptimizationStatusData {
  eligible: boolean;
  block_reason?: string | null;
  data_sources: DataSources;
  latest_snapshot?: LatestSnapshotInfo | null;
  flags: FeatureFlags;
}

// ── 2. Overview ───────────────────────────────────────────────────────────────

export interface VideoPlaceholder {
  title: string;
  url?: string | null;
}

export interface DeclineHighlight {
  keyword: string;
  position_prev: number;
  position_now?: number | null;
}

export interface CompetitorHighlight {
  domain: string;
  threat_score: number;
}

export interface OpportunityHighlight {
  keyword: string;
  score: number;
}

export interface UpdateHighlight {
  name: string;
  delta_pct?: number | null;
  impact_level: string;
}

export interface EcosystemSectionCount<T = any> {
  count: number;
  highlight?: T | null;
}

export interface EcosystemOverview {
  declines: EcosystemSectionCount<DeclineHighlight>;
  competitors: EcosystemSectionCount<CompetitorHighlight>;
  opportunities: EcosystemSectionCount<OpportunityHighlight>;
  updates: EcosystemSectionCount<UpdateHighlight>;
}

export interface ForesightLock {
  locked: boolean;
}

export interface OverviewData {
  video: VideoPlaceholder;
  ecosystem: EcosystemOverview;
  foresight: ForesightLock;
}

// ── 3. Monthly Insights ───────────────────────────────────────────────────────

export interface CountTile {
  key: 'declines' | 'competitors' | 'opportunities' | 'updates' | string;
  count: number;
  delta_vs_previous?: number | null;
}

export interface InsightSentence {
  text: string;
  section: 'declines' | 'competitors' | 'opportunities' | 'updates' | string;
}

export interface MonthlyInsightsData {
  month: string;
  available_months: string[];
  tiles: CountTile[];
  insights: InsightSentence[];
}

// ── 4. Ecosystem Summary ──────────────────────────────────────────────────────

export interface EcosystemSummaryData {
  declines: number;
  competitors: number;
  opportunities: number;
  updates: number;
}

// ── 5. Decline Alerts ─────────────────────────────────────────────────────────

export interface CauseItem {
  code: string;
  label: string;
  evidence?: Record<string, any> | null;
}

export interface DiagnosticCheck {
  findings: string[];
  next_step: string;
}

export interface Diagnostics {
  content: DiagnosticCheck;
  technical: DiagnosticCheck;
  competitor: DiagnosticCheck;
}

export interface DeclineAlertItem {
  id: string;
  keyword: string;
  page_url?: string | null;
  severity: 'critical' | 'high' | 'watch' | string;
  position_prev?: number | null;
  position_now?: number | null;
  position_delta: number;
  dropped_out: boolean;
  clicks_prev?: number | null;
  clicks_now?: number | null;
  click_loss_pct?: number | null;
  causes: CauseItem[];
  diagnostics?: Diagnostics | null;
  review_status: 'open' | 'reviewed' | string;
}

// ── 6. Emerging Competitors ───────────────────────────────────────────────────

export interface IntelBullet {
  kind: 'fact' | 'synthesis' | string;
  text: string;
}

export interface KeywordGap {
  keyword: string;
  competitor_position: number;
  user_position?: number | null;
}

export interface CompetitorItem {
  id: string;
  domain: string;
  rank_order: number;
  first_seen_at: string;
  threat_score: number;
  threat_level: 'high' | 'medium' | 'low' | string;
  shared_keywords: number;
  total_keywords: number;
  overlap_ratio: number;
  velocity: number;
  velocity_basis: 'observed' | 'historical' | 'unavailable' | string;
  domain_authority?: number | null;
  domain_authority_prev?: number | null;
  intel: IntelBullet[];
  keyword_gaps: KeywordGap[];
}

// ── 7. Growth Opportunities ───────────────────────────────────────────────────

export interface ScoreComponent {
  key: string;
  label: string;
  weight: number;
  index: number;
  points: number;
}

export interface OpportunityItem {
  id: string;
  keyword: string;
  source: 'universe' | 'gsc' | string;
  volume: number;
  volume_trend_pct: number;
  difficulty: number;
  position?: number | null;
  target_page_url?: string | null;
  impressions_30d?: number | null;
  ctr_30d?: number | null;
  engagement_rate?: number | null;
  opp_types: string[];
  score: number;
  score_components: ScoreComponent[];
  est_click_gain: number;
  recommendation: string;
  insight: string;
}

// ── 8. Algorithm Updates ──────────────────────────────────────────────────────

export interface AlgorithmUpdateItem {
  id: string;
  name: string;
  update_type: 'core' | 'spam' | 'helpful_content' | 'other' | string;
  started_at: string;
  ended_at?: string | null;
  status: 'rolling_out' | 'completed' | string;
  window_assumed: boolean;
  impact_level: 'high' | 'medium' | 'low' | 'none' | 'pending' | 'insufficient_data' | string;
  direction: 'gain' | 'loss' | 'flat' | string;
  delta_pct?: number | null;
  pages_affected?: number | null;
  pages_total?: number | null;
  adjustment_tags: string[];
  overlaps_with: string[];
  status_dashboard_url: string;
}

export interface DailyClickPoint {
  date: string;
  clicks: number;
}

export interface UpdateBand {
  update_id: string;
  short_name: string;
  update_type: string;
  from_date: string;
  to_date: string;
}

export interface TimelineData {
  from_date: string;
  to_date: string;
  daily_clicks: DailyClickPoint[];
  bands: UpdateBand[];
}

export interface AffectedPage {
  url: string;
  delta_pct: number;
  delta_clicks: number;
}

export interface AdjustmentItem {
  tag: string;
  text: string;
}

export interface AlgorithmUpdateDetail extends AlgorithmUpdateItem {
  about?: string | null;
  baseline_daily_clicks?: number | null;
  post_daily_clicks?: number | null;
  series: DailyClickPoint[];
  top_pages: AffectedPage[];
  adjustments: AdjustmentItem[];
  guidance?: string | null;
}

// ── Internal / Snapshot Management ───────────────────────────────────────────

export interface TriggerSnapshotResponse {
  status: string;
  message: string;
  snapshot_id?: string | null;
  retry_after?: number | null;
}

export interface SnapshotStatusResponse {
  snapshot_id: string;
  user_id: string;
  period_month: string;
  status: string;
  section_status: Record<string, string>;
  run_count: number;
  started_at?: string | null;
  completed_at?: string | null;
  error?: Record<string, any> | null;
}

// ── Helper Headers ────────────────────────────────────────────────────────────

function getHeaders() {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function getEffectiveUserId(userId?: string): string {
  return userId || localStorage.getItem('user_id') || '';
}

// ── Service Implementation ────────────────────────────────────────────────────

export const optimizationService = {
  /**
   * GET /optimization/status
   */
  async getStatus(userId?: string): Promise<StandardResponse<OptimizationStatusData>> {
    const uid = getEffectiveUserId(userId);
    const query = uid ? `?user_id=${encodeURIComponent(uid)}` : '';
    const res = await fetch(`${API_BASE_URL}/optimization/status${query}`, {
      headers: getHeaders(),
    });
    if (!res.ok) throw new Error(`Failed to fetch status: ${res.statusText}`);
    return res.json();
  },

  /**
   * GET /optimization/overview
   */
  async getOverview(userId?: string): Promise<StandardResponse<OverviewData>> {
    const uid = getEffectiveUserId(userId);
    const query = uid ? `?user_id=${encodeURIComponent(uid)}` : '';
    const res = await fetch(`${API_BASE_URL}/optimization/overview${query}`, {
      headers: getHeaders(),
    });
    if (!res.ok) throw new Error(`Failed to fetch overview: ${res.statusText}`);
    return res.json();
  },

  /**
   * GET /optimization/monthly-insights
   */
  async getMonthlyInsights(month?: string, userId?: string): Promise<StandardResponse<MonthlyInsightsData>> {
    const uid = getEffectiveUserId(userId);
    const params = new URLSearchParams();
    if (month) params.set('month', month);
    if (uid) params.set('user_id', uid);
    const query = params.toString() ? `?${params.toString()}` : '';

    const res = await fetch(`${API_BASE_URL}/optimization/monthly-insights${query}`, {
      headers: getHeaders(),
    });
    if (!res.ok) throw new Error(`Failed to fetch monthly insights: ${res.statusText}`);
    return res.json();
  },

  /**
   * GET /optimization/ecosystem/summary
   */
  async getEcosystemSummary(userId?: string): Promise<StandardResponse<EcosystemSummaryData>> {
    const uid = getEffectiveUserId(userId);
    const query = uid ? `?user_id=${encodeURIComponent(uid)}` : '';
    const res = await fetch(`${API_BASE_URL}/optimization/ecosystem/summary${query}`, {
      headers: getHeaders(),
    });
    if (!res.ok) throw new Error(`Failed to fetch ecosystem summary: ${res.statusText}`);
    return res.json();
  },

  /**
   * GET /optimization/declines
   */
  async getDeclines(severity?: string, userId?: string): Promise<StandardResponse<DeclineAlertItem[]>> {
    const uid = getEffectiveUserId(userId);
    const params = new URLSearchParams();
    if (severity && severity !== 'all') params.set('severity', severity);
    if (uid) params.set('user_id', uid);
    const query = params.toString() ? `?${params.toString()}` : '';

    const res = await fetch(`${API_BASE_URL}/optimization/declines${query}`, {
      headers: getHeaders(),
    });
    if (!res.ok) throw new Error(`Failed to fetch decline alerts: ${res.statusText}`);
    return res.json();
  },

  /**
   * PATCH /optimization/declines/{alertId}/review
   */
  async patchDeclineReview(alertId: string, reviewed: boolean, userId?: string): Promise<StandardResponse<DeclineAlertItem>> {
    const uid = getEffectiveUserId(userId);
    const query = uid ? `?user_id=${encodeURIComponent(uid)}` : '';
    const res = await fetch(`${API_BASE_URL}/optimization/declines/${encodeURIComponent(alertId)}/review${query}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ reviewed }),
    });
    if (!res.ok) throw new Error(`Failed to update review status: ${res.statusText}`);
    return res.json();
  },

  /**
   * GET /optimization/competitors
   */
  async getCompetitors(userId?: string): Promise<StandardResponse<CompetitorItem[]>> {
    const uid = getEffectiveUserId(userId);
    const query = uid ? `?user_id=${encodeURIComponent(uid)}` : '';
    const res = await fetch(`${API_BASE_URL}/optimization/competitors${query}`, {
      headers: getHeaders(),
    });
    if (!res.ok) throw new Error(`Failed to fetch competitors: ${res.statusText}`);
    return res.json();
  },

  /**
   * GET /optimization/opportunities
   */
  async getOpportunities(source?: string, userId?: string): Promise<StandardResponse<OpportunityItem[]>> {
    const uid = getEffectiveUserId(userId);
    const params = new URLSearchParams();
    if (source && source !== 'all') params.set('source', source);
    if (uid) params.set('user_id', uid);
    const query = params.toString() ? `?${params.toString()}` : '';

    const res = await fetch(`${API_BASE_URL}/optimization/opportunities${query}`, {
      headers: getHeaders(),
    });
    if (!res.ok) throw new Error(`Failed to fetch opportunities: ${res.statusText}`);
    return res.json();
  },

  /**
   * GET /optimization/algorithm-updates
   */
  async getAlgorithmUpdates(userId?: string): Promise<StandardResponse<AlgorithmUpdateItem[]>> {
    const uid = getEffectiveUserId(userId);
    const query = uid ? `?user_id=${encodeURIComponent(uid)}` : '';
    const res = await fetch(`${API_BASE_URL}/optimization/algorithm-updates${query}`, {
      headers: getHeaders(),
    });
    if (!res.ok) throw new Error(`Failed to fetch algorithm updates: ${res.statusText}`);
    return res.json();
  },

  /**
   * GET /optimization/algorithm-updates/timeline
   */
  async getAlgorithmUpdateTimeline(fromDate?: string, toDate?: string, userId?: string): Promise<StandardResponse<TimelineData>> {
    const uid = getEffectiveUserId(userId);
    const params = new URLSearchParams();
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    if (uid) params.set('user_id', uid);
    const query = params.toString() ? `?${params.toString()}` : '';

    const res = await fetch(`${API_BASE_URL}/optimization/algorithm-updates/timeline${query}`, {
      headers: getHeaders(),
    });
    if (!res.ok) throw new Error(`Failed to fetch timeline: ${res.statusText}`);
    return res.json();
  },

  /**
   * GET /optimization/algorithm-updates/{impactId}
   */
  async getAlgorithmUpdateDetail(impactId: string, userId?: string): Promise<StandardResponse<AlgorithmUpdateDetail>> {
    const uid = getEffectiveUserId(userId);
    const query = uid ? `?user_id=${encodeURIComponent(uid)}` : '';
    const res = await fetch(`${API_BASE_URL}/optimization/algorithm-updates/${encodeURIComponent(impactId)}${query}`, {
      headers: getHeaders(),
    });
    if (!res.ok) throw new Error(`Failed to fetch algorithm update detail: ${res.statusText}`);
    return res.json();
  },

  /**
   * POST /optimization/internal/snapshots
   */
  async triggerSnapshot(periodMonth?: string, userId?: string): Promise<TriggerSnapshotResponse> {
    const uid = getEffectiveUserId(userId);
    const res = await fetch(`${API_BASE_URL}/optimization/internal/snapshots`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        user_id: uid,
        period_month: periodMonth || null,
      }),
    });
    if (!res.ok) throw new Error(`Failed to trigger snapshot: ${res.statusText}`);
    return res.json();
  },

  /**
   * GET /optimization/internal/snapshots/{snapshotId}
   */
  async getSnapshotStatus(snapshotId: string, userId?: string): Promise<SnapshotStatusResponse> {
    const uid = getEffectiveUserId(userId);
    const query = uid ? `?user_id=${encodeURIComponent(uid)}` : '';
    const res = await fetch(`${API_BASE_URL}/optimization/internal/snapshots/${encodeURIComponent(snapshotId)}${query}`, {
      headers: getHeaders(),
    });
    if (!res.ok) throw new Error(`Failed to fetch snapshot status: ${res.statusText}`);
    return res.json();
  },
};

export default optimizationService;
